import { alpha, Box, Skeleton, useMediaQuery, useTheme } from "@mui/material";
import type { Theme } from "@mui/material";
import { CardsGrid } from "../misc/CardsGrid";
import { overviewCardStyle } from "../misc/backofficeTheme";

export function EmployeesListSkeleton(props: {
  kind?: "instructors" | "office";
  variant?: "content" | "full";
  itemCount?: number;
  showBranchFilter?: boolean;
}) {
  const { kind = "instructors", variant = "content", itemCount = 8, showBranchFilter = true } = props;
  const isMobile = useMediaQuery((theme: Theme) => theme.breakpoints.down("sm"));
  const theme = useTheme();
  const entries = Array.from({ length: itemCount }, (_value, index) =>
    isMobile ? (
      <Box
        key={index}
        data-testid="EmployeesListSkeletonItem"
        sx={{ display: "flex", gap: 1.5, borderBottom: 1, borderColor: "divider", py: 1 }}
      >
        <Skeleton variant="circular" width={40} height={40} sx={{ flexShrink: 0 }} />
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Skeleton variant="text" width="70%" sx={{ fontSize: 16, fontWeight: 600 }} />
          <Skeleton variant="text" width="55%" sx={{ fontSize: 12 }} />
        </Box>
        {kind === "instructors" && <Skeleton variant="text" width={32} sx={{ flexShrink: 0, fontSize: 14 }} />}
      </Box>
    ) : (
      <CardsGrid.Card
        key={index}
        sx={{
          backgroundColor: overviewCardStyle.backgroundColor,
          border: "1px solid",
          borderColor: alpha(theme.palette.text.primary, 0.08),
          boxShadow: "none",
          width: "100%",
          minWidth: "200px",
        }}
      >
        <Box data-testid="EmployeesListSkeletonItem">
          {kind === "instructors" && (
            <Box sx={{ display: "flex", justifyContent: "flex-end", height: 20 }}>
              <Skeleton variant="text" width={38} />
            </Box>
          )}
          <Box
            sx={{
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              gap: "8px",
              mt: kind === "instructors" ? "8px" : "28px",
              height: "192px",
            }}
          >
            <Skeleton variant="circular" width={60} height={60} sx={{ position: "relative", top: -2 }} />
            <Box>
              <Skeleton variant="text" width={105} height={20} />
              <Skeleton variant="text" width={105} height={20} />
            </Box>
            <Skeleton variant="text" width={100} sx={{ fontSize: 14 }} />
            {kind === "instructors" && <Skeleton variant="text" width={75} sx={{ fontSize: 12 }} />}
          </Box>
        </Box>
      </CardsGrid.Card>
    ),
  );

  return (
    <Box
      role="status"
      aria-label={kind === "instructors" ? "Fahrlehrer werden geladen" : "Mitarbeiter werden geladen"}
      aria-busy="true"
      data-testid="EmployeesListSkeleton"
      sx={{
        "@media (prefers-reduced-motion: reduce)": {
          "& .MuiSkeleton-root, & .MuiSkeleton-root::after": { animation: "none" },
        },
      }}
    >
      {variant === "full" && (
        <Box
          aria-hidden="true"
          data-testid="EmployeesListToolbarSkeleton"
          sx={{ display: "flex", alignItems: "center", gap: 3, overflow: "hidden", height: 64 }}
        >
          <Skeleton variant="rounded" width={isMobile ? 180 : 200} height={36} sx={{ flexShrink: 0 }} />
          {kind === "instructors" && showBranchFilter && <Skeleton variant="text" width={75} />}
        </Box>
      )}
      {isMobile ? (
        <Box aria-hidden="true" sx={{ display: "flex", flexDirection: "column", gap: 1, py: "1px" }}>
          {entries}
        </Box>
      ) : (
        <Box aria-hidden="true">
          <CardsGrid sx={{ mt: "16px", rowGap: "24px", columnGap: "24px" }}>{entries}</CardsGrid>
        </Box>
      )}
    </Box>
  );
}
